<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonCard                                            |
| Section: atoms                                                   |
| Purpose: Displays person identity cards with privacy safeguards. |
+------------------------------------------------------------------+
-->
<template>
    <a :class="['PersonCard', { 'PersonCard--small': isSmall, 'PersonCard--self': isSelf, 'PersonCard--default': isDefault }]"
        :href="resolvedHref" @click="handleCardClick">
        <div v-if="!isDefault && !isPrivate && !isPlaceholder" class="PersonCard_photo"
            :style="{ backgroundImage: photoBackgroundImage }"></div>
        <div class="PersonCard_text_wrapper">
            <h3 v-if="!isPrivate && !isPlaceholder" class="PersonCard_name">{{ name }}</h3>
            <p v-if="!isDefault && !isPrivate && !isPlaceholder" class="PersonCard_years"><img v-if="!isDefault" :src="yearsIconSrc"
                    :alt="dateIcon ? 'date' : gender" />
                {{ years }}</p>
            <p v-if="location && !isPrivate && !isPlaceholder" class="PersonCard_location"><img :src="locationIconSrc" alt="location" />{{ location }}
            </p>
            <p v-if="isPrivate || isPlaceholder" class="living">
                <img class="lock" src="@/assets/icons/lock_black.svg" alt="Privacy" />
                {{ isPlaceholder ? $t('privacy.livingPlaceholder') : $t('nav.privacyHidden') }}
            </p>
        </div>
        <img class="SelfIcon" v-if="isSelf && !isDefault && !isPrivate && !isPlaceholder" src="@/assets/icons/self.svg" alt="Self Icon" />
    </a>
</template>

<script>
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { getBackgroundImageUrl } from '@/utils/mediaUtils';

export default {
    name: 'PersonCard',
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        return { authToken, localePath };
    },
    props: {
        photoSrc: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            required: true
        },
        years: {
            type: String,
            required: true
        },
        gender: {
            type: String,
            required: true,
            validator: function (value) {
                return ['male', 'female', 'M', 'F', 'unknown'].indexOf(value) !== -1; // Added 'M' and 'F'
            }
        },
        href: {
            type: String,
            required: true
        },
        isSmall: {
            type: Boolean,
            default: false
        },
        isSelf: {
            type: Boolean,
            default: false
        },
        isDefault: {
            type: Boolean,
            default: false
        },
        location: {
            type: String,
            default: ''
        },
        dateIcon: {
            type: Boolean,
            default: false
        },
        living: {
            type: [Boolean, Number],
            default: false,
        },
        personID: {
            type: [String, Number],
            default: '',
        },
        firstname: {
            type: String,
            default: '',
        },
        lastname: {
            type: String,
            default: '',
        },
        placeholder: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        privacyPerson() {
            return {
                personID: this.personID,
                firstname: this.firstname,
                lastname: this.lastname,
                name: this.name,
                living: this.living,
            };
        },
        isPrivate() {
            return isPrivateLivingPerson(this.privacyPerson, this.authToken);
        },
        isPlaceholder() {
            return this.placeholder;
        },
        privateLoginHref() {
            const redirectPath = this.href && this.href !== 'javascript:void(0)'
                ? this.href
                : this.$route.fullPath;

            return this.$router.resolve({
                path: this.localePath('/login'),
                query: { redirect: redirectPath },
            }).href;
        },
        resolvedHref() {
            if (this.isPlaceholder) return 'javascript:void(0)';
            return this.isPrivate ? this.privateLoginHref : (this.href || 'javascript:void(0)');
        },
        displayPhotoSrc() {
            // Use the provided photoSrc or fall back to the default photo based on gender
            return this.photoSrc || getDefaultPhoto(this.gender);
        },
        photoBackgroundImage() {
            return getBackgroundImageUrl(this.displayPhotoSrc);
        },
        yearsIconSrc() {
            if (this.dateIcon) {
                return this.isSelf
                    ? require('@/assets/icons/date-white.svg')
                    : require('@/assets/icons/date.svg');
            }
            const mappedGender = this.gender === 'M' ? 'male' : this.gender === 'F' ? 'female' : this.gender;
            if (this.isSelf) {
                return mappedGender === 'male'
                    ? require('@/assets/icons/male-white.svg')
                    : require('@/assets/icons/female-white.svg');
            } else {
                return mappedGender === 'male'
                    ? require('@/assets/icons/male.svg')
                    : require('@/assets/icons/female.svg');
            }
        },
        locationIconSrc() {
            return this.isSelf
                ? require('@/assets/icons/location-white.svg')
                : require('@/assets/icons/location.svg');
        }
    },
    methods: {
        handleCardClick(event) {
            if (this.shouldUseNativeNavigation(event)) return;

            const target = this.getRouterTarget(this.resolvedHref);
            if (!target) return;

            event.preventDefault();
            this.$router.push(target);
        },
        shouldUseNativeNavigation(event) {
            return event.defaultPrevented
                || event.button !== 0
                || event.metaKey
                || event.ctrlKey
                || event.shiftKey
                || event.altKey;
        },
        getRouterTarget(href) {
            if (!href || href === 'javascript:void(0)' || href.startsWith('#')) {
                return null;
            }

            if (/^[a-z][a-z\d+\-.]*:/i.test(href)) {
                try {
                    const url = new URL(href, window.location.origin);
                    if (url.origin !== window.location.origin) return null;
                    return `${url.pathname}${url.search}${url.hash}`;
                } catch {
                    return null;
                }
            }

            return href.startsWith('/') ? href : null;
        },
    }
}
</script>

<style scoped>
.PersonCard {
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card-sm);
    background: var(--brand-bg-1);
    display: flex;
    padding: 48px;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    transition: var(--transition-bounce);
    box-shadow: 0 0 0 0px var(--brand-bg-1), var(--shadow-card-sm);
    position: relative;
}

.PersonCard:hover {
    box-shadow: 0 0 0 4px var(--brand-bg-1), var(--shadow-card-lg);
}

.PersonCard:hover h3 {
    text-decoration: underline;
}

.PersonCard_photo {
    width: 96px;
    height: 96px;
    background: var(--brand-bg-6);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    box-shadow: var(--shadow-photo);
    border-radius: var(--border-radius-pill);
}

.PersonCard--small .PersonCard_photo {
    width: 48px;
    height: 48px;
    aspect-ratio: 1 / 1;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.PersonCard_text_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.PersonCard_name {
    color: var(--brand-color-1);
    text-align: center;
}

.PersonCard_years {
    display: flex;
    align-items: center;
    gap: 8px;
}

.PersonCard_location {
    display: flex;
    align-items: center;
    gap: 8px;
    /* font-size: 12px; */
    /* color: var(--brand-bg-6); */
}

/* .PersonCard_location img { */
/* width: 12px; */
/* height: 12px; */
/* opacity: 0.6; */
/* } */

/*-----------------------------------
|    Alternative default styling    |
-----------------------------------*/

.PersonCard--default {
    background: rgba(0, 0, 0, 0);
    border: 2px dashed var(--brand-bg-1);
    color: var(--brand-text-light);
    justify-content: center;
    max-width: 100% !important;
    padding: 48px !important;
    box-shadow: none;
}

.PersonCard--default:hover {
    box-shadow: 0 0 0 0px var(--brand-bg-1);
    cursor: default;
}

.PersonCard--default .PersonCard_name {
    color: var(--brand-text-light);
    font-style: italic;
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;

}

.PersonCard--default:hover .PersonCard_name {
    text-decoration: none;
}

.PersonCard--default .PersonCard_photo,
.PersonCard--default .PersonCard_years,
.PersonCard--default .SelfIcon,
.PersonCard--default .PersonCard_years img {
    display: none;
}

/*---------------------------------
|    Alternative small styling    |
---------------------------------*/

.PersonCard--small {
    padding: 24px;
    flex-direction: row;
    position: relative;
    z-index: 1;
}

.PersonCard--small .PersonCard_text_wrapper {
    align-items: flex-start;
}

.PersonCard--small .PersonCard_name {
    font-size: 16px;
    text-align: left;
}

/*----------------------------------
|    Alternative isSelf styling    |
----------------------------------*/

.PersonCard--self {
    border: 2px solid var(--brand-bg-1);
    background-color: var(--brand-bg-4);
    position: relative;
}

.PersonCard--self .PersonCard_name,
.PersonCard--self .PersonCard_years {
    color: var(--brand-text-light);
}

.SelfIcon {
    display: flex;
    padding: 6px 12px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    position: absolute;
    top: 0;
    right: 0px;
    border-radius: 0px 8px 0px 15px;
    background: var(--brand-bg-1);
    transition: var(--transition-bounce);
}

.PersonCard:hover .SelfIcon {
    padding: 6px 12px 10px 16px;
}

.lock {
    display: none;
}

.living {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--brand-text-dark);
    text-align: left;
}

.living .lock {
    display: block;
    width: 12px;
    height: 12px;
}

@media (max-width: 860px) {
    .PersonCard .PersonCard_photo {
        width: 48px;
        height: 48px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
    }

    .PersonCard {
        padding: 24px;
        flex-direction: row;
        position: relative;
        z-index: 1;
    }

    .PersonCard .PersonCard_text_wrapper {
        align-items: flex-start;
    }

    .PersonCard .PersonCard_name {
        font-size: 16px;
        text-align: left;
    }
}
</style>
